import m from "mithril"
import { MithrilTsxComponent } from "mithril-tsx-component"
import * as State from "../../editor/state"
import { cleanString, fileToB64, createImage, memoize, UUID, createCanvas, mmToPx } from "oj-utils"
import { closest } from "oj-dom-utils"
import { IActionOption, Obj, IAction } from "../types"
import "./actionBar.scss"
import { ColorPicker } from "../../../components/ColorPicker"
import { Icon } from "../../../components/Icon"
import { buttonBounceAttrs, animateClassNames, animateCreate, animateRemove } from "oj-mithril-utils"
import { Zmdi } from "oj-mithril-packages/dist/Zmdi"
import { convertEPS } from "../api"

const handleUpload = async (v, e) => {
  const acceptedFileTypes = State.acceptedFileTypes()
  const file = e.target.files[0]

  const side = State.getSide()
  const size = Math.max(side.position.width, side.position.height)

  const pxDpiTarget = Math.round(mmToPx(size, 300))
  const pxDpiMinimum = Math.round(mmToPx(20, 300))

  try {
    if (acceptedFileTypes.includes(file.type)) {
      let img: HTMLImageElement
      let asset: string
      if (file.type.indexOf("image/") !== -1) {
        // v.attrs.onchangepending()
        img = await fileToB64(file).then(x => createImage(x))
      }
      else if (file.type.indexOf("application/") !== -1) {
        v.attrs.onchangepending()
        img = await convertPostscriptToPng(file, pxDpiTarget).then(x => createImage(x))
        asset = await fileToB64(file)
      }

      if (img.naturalWidth < pxDpiMinimum || img.naturalHeight < pxDpiMinimum) {
        v.attrs.onerror(`Please upload a bigger image file, at least ${pxDpiMinimum} x ${pxDpiMinimum} px`)
      }
      else {
        if (img.naturalWidth > pxDpiTarget || img.naturalHeight > pxDpiTarget) {
          const scale = Math.min(1, Math.max(pxDpiTarget / img.naturalWidth, pxDpiTarget / img.naturalHeight))
          const { canvas, ctx } = createCanvas({ width: img.naturalWidth * scale, height: img.naturalHeight * scale })
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
          img = await createImage(canvas.toDataURL(file.type, .9))
        }

        v.attrs.onchange(img, asset)
      }
    }
    else
      v.attrs.onerror(`Unsupported file: "${file.type}". Please upload a file of the following types: ${acceptedFileTypes.join(", ")}.`)
  } catch (e) {
    console.error(e)
  }

  e.target.value = ""
  m.redraw()
}

const convertPostscriptToPng = memoize(async (file: File, size: number) => {
  return convertEPS(file, size)
}, ([file, size]) => `${file.name}x${size}`)

class ActionOptionBarTextMobile extends MithrilTsxComponent<{ value: string | number, onchange: (value: string) => void }> {
  view(v) {
    return <textarea placeholder="Your text" value={v.attrs.value}
      oninput={e => {
        this.updateHeight(e.target)
        v.attrs.onchange(e.target.value)
      }}
    />
  }

  oncreate(v) {
    this.updateHeight(v.dom)
  }

  updateHeight(e: HTMLTextAreaElement) {
    setTimeout(() => {
      e.style.height = "auto"
      e.style.height = e.scrollHeight + "px"
    }, 0)
  }
}

class ActionOptionBarColorMobile extends MithrilTsxComponent<{ value: string | number, onchange: (value: string) => void }> {
  view(v) {
    return <div>
      <ColorPicker mobile value={v.attrs.value} oninput={v.attrs.onchange} />
      {v.attrs.options.map(x =>
        <button className={cleanString("color", x.value === v.attrs.value && "active")} onclick={e => v.attrs.onchange(x.value)}>{x.icon && <Icon icon={x.icon} />}{m.trust(x.title)}</button>
      )}
    </div>
  }
}

class ActionOptionBarImageMobile extends MithrilTsxComponent<{ onchange: (value: string, asset: string) => void, onchangepending: () => void, onerror: (err: string) => void }> {
  subs: Function[] = []

  view(v) {
    const acceptedFileTypes = State.acceptedFileTypes()

    return <label className="btn-action">
      <input style={{ display: "none" }} type="file" accept={acceptedFileTypes.join(",")} multiple={false}
        onchange={e => handleUpload(v, e)} />
      <span>Upload image</span>
    </label>
  }

  oncreate(v) {
    this.subs.push(State.ea.on("pickImage", () => v.dom.click()))
  }

  onremove(v) {
    this.subs.forEach(x => x())
  }
}

class ActionOptionBarSelectMobile extends MithrilTsxComponent<{ options: IActionOption[], value: string | number, onchange: (value: string) => void }> {
  view(v) {
    return <div>
      {v.attrs.options.map(x =>
        x.state?.hidden
          ? null
          : <button disabled={x.state?.disabled} className={cleanString(x.value === v.attrs.value && "active")} onclick={e => v.attrs.onchange(x.value)}>{x.icon && <Icon icon={x.icon} />}{m.trust(x.title)}</button>
      )}
    </div>
  }
}

class ActionOptionBarCheckboxMobile extends MithrilTsxComponent<{ options: IActionOption[], value: Obj<boolean>, onchange: (value: Obj<boolean>) => void }> {
  view(v) {
    return <div>
      {v.attrs.options.map(x =>
        x.state?.hidden
          ? null
          : <button disabled={x.state?.disabled} className={cleanString(v.attrs.value[x.value] && "active")} onclick={e => {
            const next = { ...v.attrs.value }
            next[x.value] = !next[x.value]
            v.attrs.onchange(next)
          }}>{x.icon && <Icon icon={x.icon} />}{m.trust(x.title)}</button>
      )}
    </div>
  }
}

class ActionOptionBarNumberMobile extends MithrilTsxComponent<{ value: string | number, onchange: (value: string) => void, action: IAction }> {
  bubbleButtonUp: object
  bubbleButtonDown: object
  value: number

  oninit(v) {
    this.value = v.attrs.value
    this.bubbleButtonUp = buttonBounceAttrs(e => {
      v.attrs.onchange(this.value + 1)
      m.redraw()
    }, 200)
    this.bubbleButtonDown = buttonBounceAttrs(e => {
      v.attrs.onchange(this.value - 1)
      m.redraw()
    }, 200)
  }

  onbeforeupdate(v, o) {
    this.value = v.attrs.value
  }

  view(v) {
    return <div>
      <button className="btn-action" {...this.bubbleButtonDown} disabled={v.attrs.action.state?.min} onclick={e => v.attrs.onchange(v.attrs.value - 1)}>-</button>
      <span className="btn-action-between">{Math.round(v.attrs.value)}</span>
      <button className="btn-action" {...this.bubbleButtonUp} disabled={v.attrs.action.state?.max} onclick={e => v.attrs.onchange(v.attrs.value + 1)}>+</button>
    </div>
  }
}

class ActionOptionBarTextDesktop extends MithrilTsxComponent<{ title: string, value: string | number, onchange: (value: string) => void }> {
  timeout: any
  textArea: HTMLTextAreaElement

  view(v) {
    return <div>
      <span>Text</span>
      <textarea placeholder="Your text" value={v.attrs.value}
        oncreate={e => {
          this.textArea = e.dom as HTMLTextAreaElement
          this.updateHeight(this.textArea)
          setTimeout(() => {
            this.textArea.focus()
          }, 100)
        }}
        oninput={e => {
          this.updateHeight(this.textArea)
          v.attrs.onchange(e.target.value)
        }}
      />
    </div>
  }

  updateHeight(e: HTMLTextAreaElement) {
    setTimeout(() => {
      e.style.height = "auto"
      e.style.height = e.scrollHeight + "px"
    }, 0)
  }
}

class ActionOptionBarColorDesktop extends MithrilTsxComponent<{ title: string, value: string | number, onchange: (value: string) => void }> {
  view(v) {
    return <div className="color-grid">
      <span>{v.attrs.title}</span>
      <div>
        <ColorPicker value={v.attrs.value} oninput={v.attrs.onchange} popup="bottom" />
        {v.attrs.options.map(x =>
          <button className={cleanString("color", x.value === v.attrs.value && "active")} onclick={e => v.attrs.onchange(x.value)}>{x.icon && <Icon icon={x.icon} />}{m.trust(x.title)}</button>
        )}
      </div>
    </div>
  }
}

class ActionOptionBarImageDesktop extends MithrilTsxComponent<{ onchange: (value: string, asset: string) => void, onchangepending: () => void, onerror: (err: string) => void, title: string }> {
  subs: Function[] = []

  view(v) {
    const acceptedFileTypes = State.acceptedFileTypes()

    return <div>
      <span>{v.attrs.title}</span>
      <label className="btn-action">
        <input style={{ display: "none" }} type="file" accept={acceptedFileTypes.join(",")} multiple={false}
          onchange={e => handleUpload(v, e)} />
        <span>Upload image</span>
      </label>
    </div>
  }

  oncreate(v) {
    this.subs.push(State.ea.on("pickImage", () => v.dom.querySelector("label").click()))
  }

  onremove(v) {
    this.subs.forEach(x => x())
  }
}

class ActionOptionBarSelectDesktop extends MithrilTsxComponent<{ options: IActionOption[], title: string, value: string | number, onchange: (value: string) => void, view: "select" | "grid" }> {
  open: boolean
  id = UUID()

  view(v) {

    if (v.attrs.view === "select") {
      const selected = v.attrs.options.find(x => x.value === v.attrs.value)

      return <div className="select">
        <span>{v.attrs.title}</span>
        <Zmdi icon={this.open ? "chevron-up" : "chevron-down"} />
        <div onclick={e => this.open = !this.open}>{selected ? [selected.icon && <Icon icon={selected.icon} />, m.trust(selected.title)] : "click to select"}</div>
        <div className={cleanString("options", this.open && "active")}>
          {v.attrs.options.map(x =>
            x.state?.hidden
              ? null
              : <button disabled={x.state?.disabled} className={cleanString(x.value === v.attrs.value && "active")} onclick={e => {
                this.open = false
                v.attrs.onchange(x.value)
              }}>{x.icon && <Icon icon={x.icon} />}{m.trust(x.title)}</button>
          )}
        </div>
      </div>
    }

    return <div className="grid">
      <span>{v.attrs.title}</span>
      <div>
        {v.attrs.options.map(x =>
          x.state?.hidden
            ? null
            : <button disabled={x.state?.disabled} className={cleanString("outline", x.value === v.attrs.value && "active")} onclick={e => {
              this.open = false
              v.attrs.onchange(x.value)
            }}>{x.icon && <Icon icon={x.icon} />}{m.trust(x.title)}</button>
        )}
      </div>
    </div>
  }

  oncreate(v) {
    if (v.attrs.view === "select") {
      window.on(`click.ActionOptionBarSelectDesktop.${this.id}`, e => {
        if (closest(e.target, ".select") !== v.dom)
          this.open = false
        m.redraw()
      })
    }
  }
}

class ActionOptionBarCheckboxDesktop extends MithrilTsxComponent<{ options: IActionOption[], value: Obj<boolean>, onchange: (value: Obj<boolean>) => void, title: string }> {
  view(v) {
    return <div className="grid checkbox">
      <span>{v.attrs.title}</span>
      <div>
        {v.attrs.options.map(x =>
          x.state?.hidden
            ? null
            : <button disabled={x.state?.disabled} className={cleanString("outline", v.attrs.value[x.value] && "active")} onclick={e => {
              const next = { ...v.attrs.value }
              next[x.value] = !next[x.value]
              v.attrs.onchange(next)
            }}>{x.icon && <Icon icon={x.icon} />}{m.trust(x.title)}</button>
        )}
      </div>
    </div>
  }
}

class ActionOptionBarNumberDesktop extends MithrilTsxComponent<{ action: IAction, value: string | number, onchange: (value: string) => void, title: string }> {
  bubbleButtonUp: object
  bubbleButtonDown: object
  value: number

  oninit(v) {
    this.value = v.attrs.value
    this.bubbleButtonUp = buttonBounceAttrs(e => {
      v.attrs.onchange(this.value + 1)
      m.redraw()
    }, 200)
    this.bubbleButtonDown = buttonBounceAttrs(e => {
      v.attrs.onchange(this.value - 1)
      m.redraw()
    }, 200)
  }

  onbeforeupdate(v, o) {
    this.value = v.attrs.value
  }

  view(v) {
    return <div className="number">
      <span>{v.attrs.title}</span>
      <div>
        <button className="btn-action outline" {...this.bubbleButtonDown} disabled={v.attrs.action.state?.min} onclick={e => v.attrs.onchange(v.attrs.value - 1)}>-</button>
        <span className="btn-action-between">{Math.round(v.attrs.value)}</span>
        <button className="btn-action outline" {...this.bubbleButtonUp} disabled={v.attrs.action.state?.max} onclick={e => v.attrs.onchange(v.attrs.value + 1)}>+</button>
      </div>
    </div>
  }
}

class ActionOptionBarMobile extends MithrilTsxComponent<{ action: string }> {
  view(v) {
    const action = State.getAction(v.attrs.action)
    if (!action)
      return

    return <div className="ActionOptionBar">
      {action.type === "text" && <ActionOptionBarTextMobile value={action.value} onchange={v => State.setActionValue(v)} />}
      {action.type === "image" && <ActionOptionBarImageMobile
        onchangepending={() => {
          State.setActionValue("PENDING")
          State.setActionValueAsset(undefined)
        }}
        onchange={(v, asset) => {
          State.setActionValue(v)
          State.setActionValueAsset(asset)
          State.setImageHolderRecommendedDimensions(State.getHolder())
        }}
        onerror={err => State.setDialog({
          title: "Image error", description: err, actions: [{
            title: "close", fn: () => State.setDialog()
          }, {
            title: "upload other", fn: () => {
              State.setDialog()
              State.ea.emit("pickImage")
            }
          }]
        })} />}
      {action.type === "color" && <ActionOptionBarColorMobile options={action.options} value={action.value} onchange={v => State.setActionValue(v)} />}
      {action.type === "number" && <ActionOptionBarNumberMobile action={action} value={action.value} onchange={v => State.setActionValue(v)} />}
      {action.type === "select" && action.options && <ActionOptionBarSelectMobile options={action.options} value={action.value} onchange={v => State.setActionValue(v)} />}
      {action.type === "checkbox" && action.options && <ActionOptionBarCheckboxMobile options={action.options} value={action.value} onchange={v => State.setActionValue(v)} />}
    </div>
  }
}

class ActionTypeBarMobile extends MithrilTsxComponent<{}> {
  view(v) {
    const actions = State.getHolder().actions
    const action = State.getAction()

    return <div className="ActionTypeBar">
      {actions
        .map((x, i) =>
          x.state?.hidden
            ? null
            : <button disabled={x.state?.disabled} className={cleanString(x.key === action.key && "active")} onclick={e => State.setAction(i)}>
              <Icon icon={x.icon} />
              <span>{x.title}</span>
            </button>
        )
      }
      <button className="btn-delete" onclick={e => State.showDeleteDialog(State.getHolder())}>
        <Icon icon="delete_forever" />
        <span>Delete</span>
      </button>
    </div>
  }
}


export class ActionBarMobile extends MithrilTsxComponent<{}> {
  view(v) {
    const holder = State.getHolder()
    const action = State.getAction(undefined, holder)

    return <div className={cleanString("ActionBarMobile", animateClassNames(v, "fade-from-top"))}>
      <ActionOptionBarMobile action={action.key} />
      <ActionTypeBarMobile />
    </div>
  }

  oncreate(v) {
    animateCreate(v)
  }

  onbeforeremove(v) {
    return animateRemove(v)
  }
}

export class ActionBarDesktop extends MithrilTsxComponent<{}> {
  view(v) {
    const holder = State.getHolder()

    return <div className={cleanString("ActionBarDesktop", animateClassNames(v, "fade-from-top"))}>
      <div className="actions">
        {holder.actions
          .map((x, i) => {
            if (x.state?.hiddenDesktop)
              return
            if (x.type === "text")
              return <ActionOptionBarTextDesktop value={x.value} onchange={v => State.setActionValue(v, holder, x.key)} title={x.title} />
            if (x.type === "image")
              return <ActionOptionBarImageDesktop
                onchangepending={() => {
                  State.setActionValue("PENDING", holder)
                  State.setActionValueAsset(undefined)
                }}
                onchange={(image, asset) => {
                  State.setActionValue(image, holder, x.key)
                  State.setActionValueAsset(asset)
                  State.setImageHolderRecommendedDimensions(holder)
                }}
                onerror={err => State.setDialog({
                  title: "Image error", description: err, actions: [{
                    title: "close", fn: () => State.setDialog()
                  }, {
                    title: "upload other", fn: () => {
                      State.setDialog()
                      State.ea.emit("pickImage")
                    }
                  }]
                })}
                title={x.title} />
            if (x.type === "color")
              return <ActionOptionBarColorDesktop options={x.options} value={x.value} onchange={v => State.setActionValue(v, holder, x.key)} title={x.title} />
            if (x.type === "number")
              return <ActionOptionBarNumberDesktop action={x} value={x.value} onchange={v => State.setActionValue(v, holder, x.key)} title={x.title} />
            if (x.type === "select" && x.options)
              return <ActionOptionBarSelectDesktop options={x.options} value={x.value} onchange={v => State.setActionValue(v, holder, x.key)} view={x.view} title={x.title} />
            if (x.type === "checkbox" && x.options)
              return <ActionOptionBarCheckboxDesktop options={x.options} value={x.value} onchange={v => State.setActionValue(v, holder, x.key)} title={x.title} />
          })}

        <div className="grid">
          <span>Delete</span>
          <div>
            <button className="btn-delete outline" onclick={e => State.showDeleteDialog(State.getHolder())}>
              <Icon icon="delete_forever" />
              <span>Delete</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  }

  oncreate(v) {
    animateCreate(v)
  }

  onbeforeremove(v) {
    return animateRemove(v)
  }
}